import React, { useState, useRef, useEffect, useMemo } from "react";
import { Paperclip, Inbox, Mail, Archive, Trash2, Check, Star, Loader2, ChevronDown } from "lucide-react";
import { emailColor } from "../mock";
import { intestatario } from "../intestazione";
import { SpuntaLettura } from "./Lettura";

// Favicon del dominio del mittente (quadratino sotto l'indicatore "da leggere", solo web): servita dal nostro server,
// che la scarica una volta sola e la memorizza nel DB (risposta 204 = dominio senza favicon → quadratino nascosto)
const FAVICON_KO = new Set();
function dominioDi(email) {
  const d = (email?.email || "").split("@")[1]?.trim().toLowerCase() || "";
  return d.includes(".") && !/localhost|\.local$|^\d+\.\d+/.test(d) ? d : "";
}
function Favicon({ email }) {
  const dom = dominioDi(email);
  const [ko, setKo] = useState(false);
  useEffect(() => setKo(false), [dom]);
  if (!dom || ko || FAVICON_KO.has(dom)) return null;
  return (
    <img
      className="mail-favicon"
      src={`${process.env.REACT_APP_BACKEND_URL}/api/favicon?dominio=${encodeURIComponent(dom)}`}
      alt=""
      loading="lazy"
      decoding="async"
      onError={() => { FAVICON_KO.add(dom); setKo(true); }}
      onLoad={(e) => { if (!e.target.naturalWidth) { FAVICON_KO.add(dom); setKo(true); } }}
      title={dom}
      data-testid="mail-favicon"
    />
  );
}

const LONG_PRESS_MS = 1000;

// Gruppi temporali della lista: oggi, ieri, questa settimana, questo mese, precedenti
const GRUPPI = { oggi: "Oggi", ieri: "Ieri", settimana: "Questa settimana", mese: "Questo mese", altre: "Precedenti" };
function tsEmail(e) {
  if (e.ts) return e.ts * 1000;
  const m = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec(e.data || "");
  if (!m) return 0;
  const [hh, mm] = (e.ora || "00:00").split(":").map(Number);
  return new Date(+m[3], +m[2] - 1, +m[1], hh || 0, mm || 0).getTime();
}
function gruppoDi(e) {
  const t = tsEmail(e);
  if (!t) return "altre";
  const ora = new Date();
  const inizioOggi = new Date(ora.getFullYear(), ora.getMonth(), ora.getDate()).getTime();
  const giorno = 86400000;
  if (t >= inizioOggi) return "oggi";
  if (t >= inizioOggi - giorno) return "ieri";
  if (t >= inizioOggi - 6 * giorno) return "settimana";
  if (t >= new Date(ora.getFullYear(), ora.getMonth(), 1).getTime() || t >= inizioOggi - 29 * giorno) return "mese";
  return "altre";
}

// Elemento lista con swipe destra/sinistra e pressione lunga
function SwipeItem({
  email,
  ricevute,
  termini,
  isSelected,
  onOpen,
  onPrefetch,
  onOpenFull,
  onSwipeUnread,
  onSwipeArchive,
  onSwipeDelete,
  onLongPress,
  selectMode,
  checked,
  onToggleCheck,
}) {
  const [dx, setDx] = useState(0);
  const [anim, setAnim] = useState(false);
  const wrapRef = useRef(null);
  const start = useRef(null);
  const timer = useRef(null);
  const longFired = useRef(false);
  const vertical = useRef(false);
  const lastTap = useRef(0);
  const tapTimer = useRef(null);

  const clearTimer = () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  };

  const onDown = (e) => {
    if (selectMode) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    start.current = { x: e.clientX, y: e.clientY, t: Date.now() };
    longFired.current = false;
    vertical.current = false;
    setAnim(false);
    timer.current = setTimeout(() => {
      longFired.current = true;
      setAnim(true);
      setDx(0);
      onLongPress(email);
    }, LONG_PRESS_MS);
  };

  const onMove = (e) => {
    if (!start.current || longFired.current || vertical.current) return;
    const ddx = e.clientX - start.current.x;
    const ddy = e.clientY - start.current.y;
    if (Math.abs(ddy) > 14 && Math.abs(ddy) > Math.abs(ddx)) {
      vertical.current = true;
      clearTimer();
      setDx(0);
      return;
    }
    if (Math.abs(ddx) > 10) clearTimer();
    const w = wrapRef.current ? wrapRef.current.offsetWidth : 300;
    setDx(Math.max(-w * 0.75, Math.min(ddx, 150)));
  };

  // Scorrimento iniziato (il browser annulla il puntatore): nessuna apertura
  const onCancel = () => {
    clearTimer();
    start.current = null;
    vertical.current = false;
    setAnim(true);
    setDx(0);
  };

  const onUp = (e) => {
    clearTimer();
    if (!start.current) return;
    const w = wrapRef.current ? wrapRef.current.offsetWidth : 300;
    const d = dx;
    // Tocco deciso = dito fermo (spostamento < 8px in ogni direzione) e breve (< 450 ms); scorrere non apre la mail
    const mx = Math.abs((e?.clientX ?? start.current.x) - start.current.x);
    const my = Math.abs((e?.clientY ?? start.current.y) - start.current.y);
    const durata = Date.now() - start.current.t;
    const tap = Math.abs(d) < 8 && mx < 8 && my < 8 && durata < 450 && !vertical.current && !longFired.current;
    start.current = null;
    setAnim(true);
    setDx(0);
    if (longFired.current) return;
    if (tap) {
      const now = Date.now();
      if (now - lastTap.current < 350) {
        lastTap.current = 0;
        clearTimeout(tapTimer.current);
        tapTimer.current = null;
        onOpenFull?.(email.id);
      } else {
        lastTap.current = now;
        // Su schermi piccoli la lista sparisce al primo tap: attendi un attimo per riconoscere il doppio tap
        if (onOpenFull && window.innerWidth < 1100) {
          clearTimeout(tapTimer.current);
          tapTimer.current = setTimeout(() => {
            tapTimer.current = null;
            onOpen(email.id);
          }, 280);
        } else onOpen(email.id);
      }
      return;
    }
    if (d > 90) onSwipeUnread(email);
    else if (d < -w / 2) onSwipeDelete(email);
    else if (d < -90) onSwipeArchive(email);
  };

  const w = wrapRef.current ? wrapRef.current.offsetWidth : 300;
  const halfLeft = dx < -w / 2;
  const bgColor = (() => {
    if (dx > 0) return "var(--blue-link)";
    if (dx < 0) return halfLeft ? "var(--danger)" : "#2fa64f";
    return "transparent";
  })();

  // Modalita' selezione: click semplice
  if (selectMode) {
    return (
      <div className="swipe-wrap">
        <button
          className={`mail-item ${!email.letto ? "unread" : ""} ${checked ? "selected" : ""}`}
          onClick={() => onToggleCheck(email.id)}
          data-testid={`mail-item-${email.id}`}
        >
          <span className="mail-lead">
            <span
              className="avatar-ring check-ring"
              style={{
                borderColor: checked ? "var(--ink)" : emailColor(email),
                background: checked ? "var(--yellow)" : "var(--paper)",
              }}
            >
              {checked && <Check size={18} strokeWidth={3.2} color="#122a44" />}
            </span>
            <Favicon email={email} />
          </span>
          <ItemText email={email} ricevute={ricevute} termini={termini} />
          <ItemMeta email={email} />
        </button>
      </div>
    );
  }

  return (
    <div className="swipe-wrap" ref={wrapRef}>
      <div className="swipe-bg" style={{ background: bgColor, opacity: dx === 0 ? 0 : 1 }}>
        <span className="swipe-bg-side">
          {dx > 0 && (
            <>
              <Mail size={22} strokeWidth={2.4} /> Non letta
            </>
          )}
        </span>
        <span className="swipe-bg-side">
          {dx < 0 &&
            (halfLeft ? (
              <>
                Elimina <Trash2 size={22} strokeWidth={2.4} />
              </>
            ) : (
              <>
                Archivia <Archive size={22} strokeWidth={2.4} />
              </>
            ))}
        </span>
      </div>
      <div
        className={`mail-item ${!email.letto ? "unread" : ""} ${isSelected ? "selected" : ""}`}
        role="button"
        tabIndex={0}
        style={{
          transform: `translateX(${dx}px)`,
          transition: anim ? "transform 0.22s ease" : "none",
          touchAction: "pinch-zoom pan-y",
        }}
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={onCancel}
        onMouseEnter={() => onPrefetch?.(email.id)}
        onTouchStart={() => onPrefetch?.(email.id)}
        onContextMenu={(e) => e.preventDefault()}
        onKeyDown={(e) => {
          if (e.key === "Enter") onOpen(email.id);
        }}
        data-testid={`mail-item-${email.id}`}
      >
        <span className="mail-lead">
          <span
            className={`avatar-ring ${email.letto ? "ring-letta" : ""}`}
            style={{
              borderColor: emailColor(email),
              background: !email.letto ? emailColor(email) : "var(--paper)",
            }}
          />
          <Favicon email={email} />
        </span>
        <ItemText email={email} ricevute={ricevute} termini={termini} />
        <ItemMeta email={email} />
      </div>
    </div>
  );
}

// Parole cercate evidenziate in giallo (senza distinzione di maiuscole)
function Evidenzia({ testo, termini }) {
  if (!termini?.length || !testo) return testo || "";
  const re = new RegExp(`(${termini.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`, "gi");
  return String(testo).split(re).map((p, i) => (i % 2 === 1 ? <mark key={`${i}-${p}`} className="evidenzia">{p}</mark> : p));
}

// Esito PEC sulla riga della PEC inviata: due spunte (Accettazione, Consegna) verdi quando la ricevuta è arrivata
function SpuntePec({ email, ricevute }) {
  const ha = (k) => (ricevute || []).some((r) => (r.pec_ricevuta || "").toLowerCase().includes(k));
  const negativa = (ricevute || []).find((r) => /mancata|non accettazione|anomalia|virus/i.test(r.pec_ricevuta || ""));
  const voci = [
    { k: "accettazione", label: "Accettazione", ok: ha("accettazione") && !ha("non accettazione") },
    { k: "consegna", label: "Consegna", ok: ha("consegna") && !ha("mancata consegna") },
  ];
  return (
    <span className="pec-spunte" data-testid={`pec-spunte-${email.id}`}>
      {voci.map((v) => (
        <span key={v.k} className={`pec-spunta ${v.ok ? "ok" : ""}`} title={v.ok ? `${v.label} ricevuta` : `${v.label}: in attesa`} data-testid={`pec-spunta-${v.k}-${email.id}`}>
          <Check size={11} strokeWidth={3.2} /> {v.label}
        </span>
      ))}
      {negativa && <span className="pec-spunta negativa" title={negativa.pec_ricevuta}>{negativa.pec_ricevuta}</span>}
    </span>
  );
}

function ItemText({ email, ricevute, termini }) {
  const pec = email.categoria === "pec" || !!email.pec_tipo;
  const inviataPec = pec && email.folder === "inviate";
  const anteprima = (email.corpo || "").replace(/\n+/g, " ");
  return (
    <div className="mail-item-text">
      <div className="mail-sender" data-testid="mail-sender"><Evidenzia testo={intestatario(email)} termini={termini} /></div>
      <div className={`mail-subject ${pec ? "pec" : ""}`} data-testid={pec ? "subject-pec" : undefined}>
        {email.pec_ricevuta && !ricevute?.length ? email.pec_ricevuta + ": " : ""}
        <Evidenzia testo={email.oggetto} termini={termini} />
      </div>
      <RigaAnteprima email={email} ricevute={ricevute} inviataPec={inviataPec} anteprima={anteprima} termini={termini} />
    </div>
  );
}

// Seconda riga della scheda: spunte PEC / lettura, badge ricevute oppure semplice anteprima
function RigaAnteprima({ email, ricevute, inviataPec, anteprima, termini }) {
  if (inviataPec) {
    return (
      <div className="mail-preview pec-riga">
        <SpuntePec email={email} ricevute={ricevute} />
        <SpuntaLettura email={email} />
        <span className="mail-preview-testo"><Evidenzia testo={anteprima.slice(0, 60)} termini={termini} /></span>
      </div>
    );
  }
  if (email.tracking_id && email.folder === "inviate") {
    return (
      <div className="mail-preview pec-riga">
        <SpuntaLettura email={email} />
        <span className="mail-preview-testo"><Evidenzia testo={anteprima.slice(0, 70)} termini={termini} /></span>
      </div>
    );
  }
  if (ricevute?.length > 0) {
    return (
      <div className="pec-badges" data-testid={`pec-badges-${email.id}`}>
        {ricevute.map((r) => (
          <span key={r.id} className="pec-badge">
            <Check size={11} strokeWidth={3} /> {r.pec_ricevuta}
          </span>
        ))}
      </div>
    );
  }
  return <div className="mail-preview"><Evidenzia testo={anteprima.slice(0, 90)} termini={termini} /></div>;
}

function ItemMeta({ email }) {
  return (
    <div className="mail-meta">
      <span className="mail-date" data-testid={`mail-date-${email.id}`}>{email.data}</span>
      <span className="mail-time">{email.ora}</span>
      <span className="mail-meta-icons">
        {email.preferito && <Star size={15} fill="#f6ba00" color="#f6ba00" data-testid={`star-${email.id}`} />}
        {email.allegati.length > 0 && <Paperclip size={17} strokeWidth={2.4} />}
      </span>
    </div>
  );
}

export default function EmailList({
  emails,
  ricevuteDi,
  termini,
  hasMore,
  loadingMore,
  onLoadMore,
  selectedId,
  onSelect,
  onPrefetch,
  onOpenFull,
  onSwipeUnread,
  onSwipeArchive,
  onSwipeDelete,
  onLongPress,
  selectMode,
  checkedIds,
  onToggleCheck,
  loading,
  onEmptyTrash,
}) {
  if (emails.length === 0) {
    return (
      <div className="email-list" data-testid="email-list">
        {loading ? (
          <div className="loading-state" data-testid="loading-state">
            <Loader2 size={38} strokeWidth={2} className="spin-icon" />
            <span>Caricamento posta...</span>
          </div>
        ) : (
          <div className="empty-state" data-testid="empty-state">
            <Inbox size={44} strokeWidth={1.8} />
            <span>Nessuna email in questa cartella</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <ListaVirtuale
      emails={emails}
      hasMore={hasMore}
      loadingMore={loadingMore}
      onLoadMore={onLoadMore}
      onEmptyTrash={onEmptyTrash}
      renderRiga={(e, i) => {
        const g = gruppoDi(e);
        const nuovo = i === 0 || g !== gruppoDi(emails[i - 1]);
        return (
          <React.Fragment key={e.id}>
            {nuovo && (
              <div className="list-group" data-testid={`list-group-${g}`}>
                {GRUPPI[g]}
              </div>
            )}
            <SwipeItem
              email={e}
              ricevute={ricevuteDi?.[e.id]}
              termini={termini}
              isSelected={selectedId === e.id}
              onOpen={onSelect}
              onPrefetch={onPrefetch}
              onOpenFull={onOpenFull}
              onSwipeUnread={onSwipeUnread}
              onSwipeArchive={onSwipeArchive}
              onSwipeDelete={onSwipeDelete}
              onLongPress={onLongPress}
              selectMode={selectMode}
              checked={checkedIds.includes(e.id)}
              onToggleCheck={onToggleCheck}
            />
          </React.Fragment>
        );
      }}
    />
  );
}

// Lista virtuale: disegna solo le righe visibili (più un margine) in base allo scroll; le altre sono
// sostituite da spazi vuoti dell'altezza giusta. L'altezza di riga viene misurata sulle righe reali.
const ALTEZZA_RIGA_STIMATA = 72;
const ALTEZZA_GRUPPO = 30;
const MARGINE_RIGHE = 8;

function ListaVirtuale({ emails, hasMore, loadingMore, onLoadMore, onEmptyTrash, renderRiga }) {
  const ref = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [altezzaVista, setAltezzaVista] = useState(800);
  const [altezzaRiga, setAltezzaRiga] = useState(ALTEZZA_RIGA_STIMATA);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    setAltezzaVista(el.clientHeight);
    const ro = new ResizeObserver(() => setAltezzaVista(el.clientHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // misura l'altezza reale di una riga (varia tra web e PWA)
  const [altezzaGruppo, setAltezzaGruppo] = useState(ALTEZZA_GRUPPO);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const righe = el.querySelectorAll(".swipe-wrap");
    for (let i = 0; i + 1 < righe.length; i++) {
      if (righe[i].nextElementSibling === righe[i + 1]) {
        const h = righe[i + 1].offsetTop - righe[i].offsetTop;
        if (h > 20 && Math.abs(h - altezzaRiga) > 0.5) setAltezzaRiga(h);
        break;
      }
    }
    const g = el.querySelector(".list-group");
    if (g) {
      const st = getComputedStyle(g);
      const hg = g.getBoundingClientRect().height + parseFloat(st.marginTop || 0) + parseFloat(st.marginBottom || 0);
      if (hg > 10 && Math.abs(hg - altezzaGruppo) > 0.5) setAltezzaGruppo(hg);
    }
  }, [emails.length, altezzaRiga, altezzaGruppo]);

  // gruppi: quante intestazioni precedono ogni riga (per l'offset)
  const gruppiPrima = useMemo(() => {
    const out = new Array(emails.length);
    let n = 0;
    for (let i = 0; i < emails.length; i++) {
      if (i === 0 || gruppoDi(emails[i]) !== gruppoDi(emails[i - 1])) n++;
      out[i] = n;
    }
    return out;
  }, [emails]);
  const altezzaTotale = emails.length * altezzaRiga + (gruppiPrima[emails.length - 1] || 0) * altezzaGruppo;

  // indice della prima riga visibile (ricerca binaria sull'offset)
  const offsetDi = (i) => i * altezzaRiga + (i > 0 ? gruppiPrima[i - 1] : 0) * altezzaGruppo;
  let lo = 0, hi = emails.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (offsetDi(mid) <= scrollTop) lo = mid; else hi = mid - 1;
  }
  const inizio = Math.max(0, lo - MARGINE_RIGHE);
  const fine = Math.min(emails.length, lo + Math.ceil(altezzaVista / altezzaRiga) + MARGINE_RIGHE * 2);
  const sopra = offsetDi(inizio);
  const sotto = Math.max(0, altezzaTotale - offsetDi(fine));

  const onScroll = (ev) => {
    const el = ev.currentTarget;
    setScrollTop(el.scrollTop);
    if (hasMore && !loadingMore && el.scrollHeight - el.scrollTop - el.clientHeight < 240) onLoadMore?.();
  };

  return (
    <div className="email-list" ref={ref} onScroll={onScroll} data-testid="email-list" data-righe-visibili={fine - inizio}>
      {onEmptyTrash && (
        <button className="empty-trash-btn" onClick={onEmptyTrash} data-testid="empty-trash-btn">
          <Trash2 size={17} strokeWidth={2.5} /> Svuota Eliminate ({emails.length})
        </button>
      )}
      {sopra > 0 && <div style={{ height: sopra }} aria-hidden="true" data-testid="lista-spazio-sopra" />}
      {emails.slice(inizio, fine).map((e, k) => renderRiga(e, inizio + k))}
      {sotto > 0 && <div style={{ height: sotto }} aria-hidden="true" data-testid="lista-spazio-sotto" />}
      {hasMore ? (
        <div className="load-more-wrap">
          <button className="wf-btn" onClick={onLoadMore} disabled={loadingMore} data-testid="load-more-btn">
            {loadingMore ? <Loader2 size={16} className="spin-icon" /> : <ChevronDown size={16} strokeWidth={2.6} />}
            {loadingMore ? "Caricamento..." : "Carica altre email"}
          </button>
        </div>
      ) : (
        emails.length > 0 && onLoadMore && <div className="load-more-end" data-testid="load-more-end">Tutta la posta è caricata</div>
      )}
    </div>
  );
}
